import React, { useState, useEffect, useMemo, useRef } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import "../index.css";
import buttonBackground from "../assets/button_background_blue.webp";

interface ValidationError {
  title: string;
  message: string;
}

// Type definitions
interface TreeNode {
  id: string;
  name: string;
  children?: TreeNode[];
  icon?: string;
}

interface CheckboxTreeProps {
  sportsMetadata: Record<string, unknown>;
  formState: number;
  setFormState: React.Dispatch<React.SetStateAction<number>>;
  teams: { [key: string]: boolean };
  setTeams: (teams: string[]) => void;
  ruleType?: number;
  playerMetadata?: Record<string, unknown>;
}

function buildPlayersByTeam(
  playerMetadata: Record<string, unknown>,
): Record<string, TreeNode[]> {
  const result: Record<string, TreeNode[]> = {};
  Object.entries(playerMetadata).forEach(([playerId, rawData]) => {
    let teamId: string;
    let playerName: string;
    if (typeof rawData === "string" || typeof rawData === "number") {
      teamId = String(rawData);
      playerName = playerId;
    } else if (rawData && typeof rawData === "object") {
      const pd = rawData as Record<string, unknown>;
      teamId = String(pd["team_id"] ?? pd["team"] ?? "");
      playerName = (pd["display_name"] as string) || (pd["name"] as string) || playerId;
    } else {
      return;
    }
    if (!teamId) return;
    if (!result[teamId]) result[teamId] = [];
    result[teamId].push({ id: `P${playerId}`, name: playerName });
  });
  Object.values(result).forEach((players) =>
    players.sort((a, b) => a.name.localeCompare(b.name)),
  );
  return result;
}

function augmentTreeWithPlayers(
  nodes: TreeNode[],
  playersByTeam: Record<string, TreeNode[]>,
): TreeNode[] {
  return nodes.map((node) => {
    if (node.children && node.children.length > 0) {
      return { ...node, children: augmentTreeWithPlayers(node.children, playersByTeam) };
    }
    const players = playersByTeam[String(node.id)] ?? [];
    return players.length > 0 ? { ...node, children: players } : node;
  });
}

interface CheckedItems {
  [key: string]: boolean;
}

interface IndeterminateItems {
  [key: string]: boolean;
}

interface ExpandedItems {
  [key: string]: boolean;
}

// Get this node's id plus every descendant id (conference → division → team →
// player), used both to cascade a checkbox toggle down and to re-expand a
// pruned selection back out for display.
function getDescendantIds(node: TreeNode): string[] {
  let ids = [node.id];
  if (node.children) {
    node.children.forEach((child) => {
      ids = [...ids, ...getDescendantIds(child)];
    });
  }
  return ids;
}

// A saved rule's `teams` list only records the highest-level id that covers a
// fully-selected branch (e.g. just the conference, not every division/team/
// player under it) — see collectMinimalSelection. To show the tree correctly
// on edit, re-expand each recorded id back into itself plus every descendant.
function expandSelection(
  selected: Record<string, boolean>,
  nodes: TreeNode[],
): CheckedItems {
  const result: CheckedItems = {};
  const walk = (list: TreeNode[]) => {
    list.forEach((node) => {
      if (selected[node.id]) {
        getDescendantIds(node).forEach((id) => {
          result[id] = true;
        });
      } else if (node.children) {
        walk(node.children);
      }
    });
  };
  walk(nodes);
  return result;
}

// Inverse of expandSelection: walks the tree top-down and, for the submitted
// `teams` list, keeps only the highest-level id needed to cover each fully-
// selected branch — a fully-checked conference contributes just its own id
// (not every division/team/player beneath it), same for a fully-checked
// division or (in player mode) a fully-checked team's players. Only
// indeterminate branches are descended into, to find their selected parts.
function collectMinimalSelection(
  nodes: TreeNode[],
  checked: CheckedItems,
  indeterminate: IndeterminateItems,
): string[] {
  const result: string[] = [];
  nodes.forEach((node) => {
    if (checked[node.id]) {
      result.push(node.id);
    } else if (indeterminate[node.id] && node.children) {
      result.push(...collectMinimalSelection(node.children, checked, indeterminate));
    }
  });
  return result;
}

// Custom Checkbox component for React web
interface CheckboxProps {
  checked: boolean;
  indeterminate?: boolean;
  onPress: () => void;
}

const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  indeterminate,
  onPress,
}) => {
  return (
    <button
      onClick={onPress}
      style={{
        padding: 0,
        marginRight: 10,
        background: "none",
        border: "none",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          width: 20,
          height: 20,
          borderWidth: 2,
          borderStyle: "solid",
          borderRadius: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderColor: checked || indeterminate ? "#007AFF" : "#ccc",
          backgroundColor: checked || indeterminate ? "#007AFF" : "#fff",
        }}
      >
        {checked && !indeterminate && (
          <span
            style={{
              color: "#fff",
              fontSize: 12,
              fontWeight: "bold",
            }}
          >
            ✓
          </span>
        )}
        {indeterminate && (
          <span
            style={{
              color: "#fff",
              fontSize: 12,
              fontWeight: "bold",
            }}
          >
            -
          </span>
        )}
      </div>
    </button>
  );
};

// Main checkbox tree component
const CheckboxTree: React.FC<CheckboxTreeProps> = ({
  sportsMetadata,
  formState,
  setFormState,
  teams,
  setTeams,
  ruleType,
  playerMetadata,
}) => {
  const isPlayerMode = ruleType === 5;

  const treeData = useMemo((): TreeNode[] => {
    const baseNodes = sportsMetadata as unknown as TreeNode[];
    if (!isPlayerMode || !playerMetadata || Object.keys(playerMetadata).length === 0) {
      return baseNodes;
    }
    const playersByTeam = buildPlayersByTeam(playerMetadata);
    const augmented = augmentTreeWithPlayers(baseNodes, playersByTeam);
    return augmented;
  }, [sportsMetadata, isPlayerMode, playerMetadata]);

  const treeDataRef = useRef<TreeNode[]>(treeData);
  treeDataRef.current = treeData;

  const [checkedItems, setCheckedItems] = useState<CheckedItems>(() =>
    expandSelection(teams, treeData),
  );
  const [indeterminateItems, setIndeterminateItems] =
    useState<IndeterminateItems>({});
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    () => {
      const result: ExpandedItems = {};
      const init = (nodes: TreeNode[]) => {
        nodes.forEach((node) => {
          if (node.children && node.children.length > 0) {
            result[node.id] = node.id === "ALL";
            init(node.children);
          }
        });
      };
      init(treeData);
      return result;
    },
  );
  const [validationError, setValidationError] =
    useState<ValidationError | null>(null);

  // Handle checkbox change
  const handleCheckboxChange = (node: TreeNode, isChecked: boolean): void => {
    // Create a new checked items state
    const newCheckedItems = { ...checkedItems };

    // Get all descendant IDs
    const descendantIds = getDescendantIds(node);

    // Update checked status for this node and all its descendants
    descendantIds.forEach((id) => {
      newCheckedItems[id] = isChecked;
    });

    setCheckedItems(newCheckedItems);

    // Update indeterminate state and parent checked state
    updateIndeterminateState(newCheckedItems);
  };

  // Update indeterminate state and parent checked state
  const updateIndeterminateState = (checkedState: CheckedItems): void => {
    const newIndeterminateItems: IndeterminateItems = {};
    const newCheckedItems = { ...checkedState };

    // Process each node to determine indeterminate state from bottom up
    const processNodes = (nodes: unknown): void => {
      (nodes as TreeNode[]).forEach((node: TreeNode) => {
        if (node.children && node.children.length > 0) {
          // Process children first (bottom-up approach)
          processNodes(node.children);

          const childIds = node.children.map((child: TreeNode) => child.id);
          const allChildrenChecked = childIds.every(
            (id: string) => newCheckedItems[id],
          );
          const someChildrenChecked = childIds.some(
            (id: string) => newCheckedItems[id] || newIndeterminateItems[id],
          );

          // Set parent checked state based on children
          if (allChildrenChecked) {
            newCheckedItems[node.id] = true;
          } else {
            newCheckedItems[node.id] = false;
          }

          // Set indeterminate state
          newIndeterminateItems[node.id] =
            !allChildrenChecked && someChildrenChecked;
        }
      });
    };

    processNodes(treeDataRef.current);
    setCheckedItems(newCheckedItems);
    setIndeterminateItems(newIndeterminateItems);
  };

  // Initialize indeterminate state
  useEffect(() => {
    const handleUpdate = (): void => {
      const newCheckedItems = { ...checkedItems };
      updateIndeterminateState(newCheckedItems);
    };

    handleUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toggle node expansion
  const toggleNodeExpansion = (nodeId: string): void => {
    setExpandedItems((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId],
    }));
  };

  const handlePrevious = () => {
    setFormState(formState - 1);
  };

  const handleNext = () => {
    const minimalSelection = collectMinimalSelection(
      treeData,
      checkedItems,
      indeterminateItems,
    );

    if (minimalSelection.length === 0) {
      setValidationError({
        title: isPlayerMode ? "Player Selection Required" : "Team Selection Required",
        message: isPlayerMode
          ? "Please select at least one player to continue."
          : "Please select at least one team to continue.",
      });
      return;
    }

    setTeams(minimalSelection);
    setFormState(formState + 1);
  };

  // Recursive component to render each node
  interface TreeNodeComponentProps {
    node: TreeNode;
    level?: number;
  }

  const TreeNodeComponent: React.FC<TreeNodeComponentProps> = ({
    node,
    level = 0,
  }) => {
    const isChecked = !!checkedItems[node.id];
    const isIndeterminate = !!indeterminateItems[node.id];
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedItems[node.id] !== false; // Default to expanded

    return (
      <div style={{ marginBottom: 5, marginLeft: level === 0 ? 0 : 20 }}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            // paddingTop: 5,
            // paddingBottom: 5,
          }}
        >
          {hasChildren ? (
            <button
              onClick={() => toggleNodeExpansion(node.id)}
              style={{
                padding: 5,
                marginRight: 5,
                background: "none",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              {isExpanded ? (
                <ChevronDown size={16} color="#FFF" />
              ) : (
                <ChevronRight size={16} color="#FFF" />
              )}
            </button>
          ) : (
            <div style={{ width: 26, marginRight: 5, flexShrink: 0 }} />
          )}

          <Checkbox
            checked={isChecked}
            indeterminate={isIndeterminate}
            onPress={() => handleCheckboxChange(node, !isChecked)}
          />

          <button
            onClick={() => handleCheckboxChange(node, !isChecked)}
            style={{
              flex: 1,
              background: "none",
              border: "none",
              cursor: "pointer",
              textAlign: "left",
              padding: 0,
            }}
          >
            <span
              className="font_family_base detail_text"
              style={{
                fontSize: 16,
                // color: "#FFF",
              }}
            >
              {node.name}
            </span>
          </button>
        </div>

        {hasChildren && isExpanded && (
          <div>
            {node.children!.map((child) => (
              <TreeNodeComponent
                key={child.id}
                node={child}
                level={level + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Initialize expanded state for all parent nodes
  useEffect(() => {
    const initialExpandedState: ExpandedItems = {};

    const initializeExpandedState = (nodes: unknown): void => {
      (nodes as TreeNode[]).forEach((node: TreeNode) => {
        if (node.children && node.children.length > 0) {
          initialExpandedState[node.id] = node.id === "ALL"; // Set all nodes to expanded by default
          initializeExpandedState(node.children);
        }
      });
    };

    initializeExpandedState(treeData);
    setExpandedItems(initialExpandedState);
  }, [treeData]);

  return (
    <>
      <div
        style={{
          marginTop: "10px",
          padding: "0 10px",
          maxWidth: 800,
          margin: "0 auto",
        }}
      >
        <div className=" border_radius" style={{}}>
          <div className="center_me">
            <div
              className=" border_radius center_me"
              style={{
                // backgroundColor: "#2a2a2a",
                // borderRadius: 8,
                marginTop: 10,
                paddingLeft: 15,
                paddingRight: 15,
              }}
            >
              <h2
                className="font_family_base text_green center_me "
                style={{
                  fontSize: 20,
                  fontWeight: "bold",
                  marginBottom: 10,
                  textAlign: "center",
                  marginTop: 8,
                }}
              >
                {isPlayerMode ? "Select Players to Follow" : "Select Teams to Follow"}
              </h2>
            </div>
          </div>
          <div style={{ padding: 20 }}>
            {treeData.map((node: TreeNode) => (
              <TreeNodeComponent key={node.id} node={node} />
            ))}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            gap: 16,
            paddingLeft: 20,
            paddingRight: 20,
            paddingTop: 20,
            paddingBottom: 20,
          }}
        >
          <button
            className="my-form-button button-base button-3d font_family_base"
            style={{
              marginTop: "5px",
              marginBottom: "5px",
              backgroundImage: `url(${buttonBackground})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              color: "white",
              fontWeight: 700,
            }}
            onClick={handlePrevious}
          >
            ← Previous
          </button>
          <button
            className="my-form-button button-base button-3d font_family_base"
            style={{
              marginTop: "5px",
              marginBottom: "5px",
              backgroundImage: `url(${buttonBackground})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              color: "white",
              fontWeight: 700,
            }}
            onClick={handleNext}
          >
            Next →
          </button>
        </div>
      </div>

      {/* Validation Error Modal */}
      {validationError && (
        <div
          onClick={() => setValidationError(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="background_2 border_radius"
            style={{
              padding: 30,
              maxWidth: 400,
              width: "90%",
              textAlign: "center",
              border: "1px solid #333",
            }}
          >
            <h3
              className="font_family_base"
              style={{
                color: "#fff",
                marginBottom: 15,
                fontSize: 20,
              }}
            >
              {validationError.title}
            </h3>
            <p
              className="font_family_base"
              style={{
                color: "#ccc",
                marginBottom: 25,
                fontSize: 16,
                lineHeight: 1.5,
              }}
            >
              {validationError.message}
            </p>
            <button
              onClick={() => setValidationError(null)}
              className="my-form-button-wide button-base button-3d font_family_base"
              style={{
                backgroundImage: `url(${buttonBackground})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                color: "white",
                fontWeight: 700,
                padding: "12px 24px",
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default CheckboxTree;
